import { useState } from "react";
import { animated, easings, useSpring } from "@react-spring/web";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";

type AILoadingProps = {
  startTime: number | undefined;
  estimatedDuration: number;
  isDone?: boolean;
};

const STEPS = [
  "Thinking",
  "making your design print-ready",
  "sharpening your image clarity",
  "making your background removable",
  "enriching your colors",
  "smoothing your image edges",
  "running a final quality check"
];

export function AILoading(props: AILoadingProps) {
  const { startTime = 0, estimatedDuration, isDone = false } = props;
  const { isMobile } = useResponsive();

  const elapsed = Math.max(0, Date.now() - startTime);
  const start = Math.min(1, elapsed / estimatedDuration); // 0-1, capped at 1

  const total = STEPS.length;

  const [currentIndex, setCurrentIndex] = useState(() =>
    Math.min(total - 1, Math.floor(start * total))
  );

  const currentStep = STEPS[currentIndex];
  const upperBoundSeconds = Math.round(estimatedDuration / 1000);
  const estimatedTimeText = isMobile
    ? `Up to ${upperBoundSeconds}s (usually less)`
    : `Estimated time: up to ${upperBoundSeconds} seconds (usually less)`;

  const { progress } = useSpring({
    from: { progress: start },
    to: { progress: 0.98 },
    config: {
      duration: estimatedDuration,
      mass: 2,
      tension: 300,
      friction: 15,
      easing: easings.easeOutQuad
    },
    onChange: ({ value }) => {
      const p = value["progress"];
      const idx = Math.min(total - 1, Math.floor(p * total));
      setCurrentIndex(idx);
    }
  });

  return (
    <div
      className="text-center text-sm font-semibold leading-tight text-gray-900"
      data-testid="ai-loading-container"
    >
      {isDone ? (
        <>
          <span className="text-purple-500">All done!</span> It&apos;s print perfect now.
        </>
      ) : (
        <>
          {currentIndex ? (
            <>
              <span className="text-purple-500">I&apos;m</span> {currentStep}
            </>
          ) : (
            currentStep
          )}
          ...
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
            <animated.div
              data-testid="ai-loading-progress-bar"
              className="h-full rounded-full bg-blue-600"
              style={{
                width: progress.to(p => `${Math.min(100, p * 100)}%`)
              }}
            />
          </div>
          <div
            className="mt-2 text-center text-xs font-normal text-gray-500 md:text-left"
            data-testid="ai-loading-estimated-time"
          >
            {estimatedTimeText}
          </div>
        </>
      )}
    </div>
  );
}
